import { Button, Field, Input } from "@bafe/ui";
import { checkAccountPassword } from "@bafe/validation";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";

import { AuthShell } from "../../components/AuthShell";
import { ErrorPanel } from "../../components/ErrorPanel";
import { PasswordField } from "../../components/PasswordField";
import { api } from "../../lib/api";
import { useCompleteSignIn } from "../../lib/auth-flow";
import { fieldError } from "../../lib/errors";

function detectTimezone(): string | undefined {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return undefined;
	}
}

export function Register() {
	const completeSignIn = useCompleteSignIn();

	const [fullName, setFullName] = useState("");
	const [organisationName, setOrganisationName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [touchedPassword, setTouchedPassword] = useState(false);

	const passwordCheck = checkAccountPassword(password);
	const passwordError = touchedPassword && !passwordCheck.ok ? (passwordCheck.message ?? null) : null;

	const register = useMutation({
		mutationFn: () => {
			const timezone = detectTimezone();
			return api.register({
				fullName: fullName.trim(),
				email: email.trim(),
				password,
				organisationName: organisationName.trim(),
				...(timezone ? { timezone } : {}),
			});
		},
		onSuccess: (session) => completeSignIn(session, "/"),
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setTouchedPassword(true);
		if (!passwordCheck.ok) return;
		register.mutate();
	}

	return (
		<AuthShell
			title="Create your Key account"
			description="This also creates your organisation. You can add people to it afterwards."
			footer={
				<>
					Already have an account?{" "}
					<Link to="/signin" className="font-medium text-(--color-brand-700) underline underline-offset-2">
						Sign in
					</Link>
				</>
			}
		>
			<form className="flex flex-col gap-4" onSubmit={submit} noValidate>
				<Field label="Your name" error={fieldError(register.error, "fullName")} required>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							value={fullName}
							required
							autoComplete="name"
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setFullName(event.target.value)}
						/>
					)}
				</Field>

				<Field
					label="Organisation name"
					help="The business or team this account belongs to."
					error={fieldError(register.error, "organisationName")}
					required
				>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							value={organisationName}
							required
							autoComplete="organization"
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setOrganisationName(event.target.value)}
						/>
					)}
				</Field>

				<Field
					label="Email address"
					help="Where we send account and billing notices. It does not need to be on your domain."
					error={fieldError(register.error, "email")}
					required
				>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							type="email"
							value={email}
							required
							autoComplete="username"
							autoCapitalize="none"
							spellCheck={false}
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setEmail(event.target.value)}
						/>
					)}
				</Field>

				<PasswordField
					label="Password"
					value={password}
					onChange={(value) => {
						setPassword(value);
						setTouchedPassword(true);
					}}
					error={passwordError ?? fieldError(register.error, "password")}
					help="At least 10 characters. Longer is better than complicated."
					required
				/>

				{register.isError && <ErrorPanel error={register.error} title="We could not create your account." />}

				<Button
					type="submit"
					variant="primary"
					fullWidth
					pending={register.isPending}
					pendingLabel="Creating your account…"
				>
					Create account
				</Button>
			</form>
		</AuthShell>
	);
}
